import { useState, useCallback, useContext } from 'react';

import { type ExcalidrawImageElement, type FileId } from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFileData, type DataURL } from '@excalidraw/excalidraw/types/types';
import { nanoid } from 'nanoid';
import { type TextContent, type TextItem } from 'pdfjs-dist/types/src/display/api';
import { createContainer } from 'unstated-next';

import { WhiteboardContext } from './WhiteboardContext';
import { WhiteboardSessionContext } from './WhiteboardSessionContext';
import { Status } from '../../../types/apiTypes';
import { UserRoles } from '../../roster/types';
import { getPdfFile, type PdfPage, type PdfPageImage } from '../components/interface/import/getPdfFile';
import { type Page, PageTypes, type Session, SessionTypes } from '../types';

/**
 * Concurrency limit for uploaded files.
 */
const IMAGE_UPLOAD_BATCH_SIZE = 2;

interface ImageElementWithKey extends ExcalidrawImageElement {
    key: string;
}

const useWhiteboardPdf = () => {
    const { getPages, deletePages, createPages, uploadImage, saveElements, updatePages, getElements, deleteElements } =
        useContext(WhiteboardContext);
    const { whiteboardSessionState, excalidrawAPIRef } = useContext(WhiteboardSessionContext);

    const [total, setTotal] = useState(0);
    const [progress, setProgress] = useState(0);
    const [fileStatus, setFileStatus] = useState<Status>(Status.NotStarted);

    /**
     * Delete all pages in a PDF.
     */
    const deletePdf = useCallback(
        async (session: Session, fileName: string) => {
            const allPages = await getPages(session.session_id);
            const toDelete: string[] = [];

            if (session.type === SessionTypes.Individual) {
                const templatePage = allPages.filter((page) => page.type === PageTypes.Template)[0];
                if (templatePage) {
                    const allElements = await getElements(templatePage);
                    const elementsToDelete = allElements.filter((element) => element.body?.customData?.pdfPage);
                    if (elementsToDelete.length > 0) {
                        await deleteElements(elementsToDelete.map((element) => element.element_id));
                    }
                }
            } else if (session.type === SessionTypes.Breakout) {
                const templatePage = allPages.filter((page) => page.type === PageTypes.Template)[0];
                if (templatePage) {
                    const allElements = await getElements(templatePage);
                    const elementsToDelete = allElements.filter((element) => element.body?.customData?.pdfPage);
                    if (elementsToDelete.length > 0) {
                        await deleteElements(elementsToDelete.map((element) => element.element_id));
                    }
                }
            } else {
                allPages.forEach((page) => {
                    if (page.name === fileName) {
                        toDelete.push(page.page_id);
                    }
                });

                if (!toDelete.length) return;

                await deletePages(toDelete);
            }
        },
        [deletePages, getPages, getElements, deleteElements]
    );

    /**
     * Remove a PDF if it already exists in the whiteboard.
     */
    const deletePreviousPdf = useCallback(
        async (session, fileName) => {
            await deletePdf(session, fileName);
        },
        [deletePdf]
    );

    /**
     * Create all unprocessed PDF pages up front. PDF pages with negative
     * order are excluded when displaying page list. Order is negated
     * after post-processing is complete.
     */
    const createPdfPages = useCallback(
        async (session, fileName, totalPages: number) => {
            const allPages = await getPages(session.session_id);
            const lastPageOrder = Math.max(...allPages.map((page) => page.order));

            const pagePayloads: Partial<Page>[] = [];

            for (let i = 0; i < totalPages; i += 1) {
                pagePayloads.push({
                    session_id: session.session_id,
                    name: fileName,
                    is_locked: false,
                    write_access: UserRoles.Teacher,
                    type: PageTypes.Pdf,
                    order: -(lastPageOrder + 1 + i)
                });
            }

            return createPages(pagePayloads);
        },
        [createPages, getPages]
    );

    async function getPdfPageImages(pages: PdfPage[]) {
        const pdfPagesImagePromises: Promise<PdfPageImage>[] = [];

        for (let i = 0; i < pages.length; i += 1) {
            pdfPagesImagePromises.push(pages[i].getPageImage());
        }

        return (
            (await Promise.allSettled(pdfPagesImagePromises)) as {
                status: 'fulfilled' | 'rejected';
                value: PdfPageImage;
            }[]
        )
            .filter(({ status }) => status === 'fulfilled')
            .map((p) => p.value);
    }

    const getPdfPagesTextContents = async (pages: PdfPage[]) => {
        const pdfPagesTextContents: Promise<TextContent>[] = [];

        for (let i = 0; i < pages.length; i += 1) {
            pdfPagesTextContents.push(pages[i].getPageTextContent());
        }

        return (
            (await Promise.allSettled(pdfPagesTextContents)) as {
                status: 'fulfilled' | 'rejected';
                value: TextContent;
            }[]
        )
            .filter(({ status }) => status === 'fulfilled')
            .map((p) => p.value);
    };

    /**
     * Uploads a PDF page as an image to S3, then creates an
     * image element with that key for the corresponding PDF
     * whiteboard page.
     */
    const upload = useCallback(
        async (page: Page, image, textContent: TextContent) => {
            const fileId = nanoid(40);

            const pageText = textContent.items.map((item) => (item as TextItem).str);

            const file: BinaryFileData = {
                mimeType: 'image/png',
                created: Date.now(),
                id: fileId as FileId,
                dataURL: image?.blobUrl as DataURL
            };

            await uploadImage(page, file);

            const previewFile: BinaryFileData = {
                mimeType: 'image/png',
                created: Date.now(),
                id: `${fileId}-preview` as FileId,
                dataURL: image?.previewBlobUrl as DataURL
            };

            await uploadImage(page, previewFile);

            const element: ImageElementWithKey = {
                type: 'image',
                x: 100,
                y: 100,
                angle: 0,
                backgroundColor: 'transparent',
                boundElements: null,
                fillStyle: 'hachure',
                groupIds: [],
                isDeleted: false,
                link: null,
                locked: true,
                opacity: 100,
                roughness: 1,
                roundness: { type: 1 },
                seed: 0,
                status: 'saved',
                strokeColor: 'transparent',
                strokeStyle: 'solid',
                strokeWidth: 1,
                version: 1,
                versionNonce: 1,
                scale: [1, 1],
                width: image!.width,
                height: image!.height,
                id: fileId,
                fileId: fileId as FileId,
                key: `${fileId}.png`,
                updated: file.created,
                customData: {
                    pageText,
                    pageTextContent: textContent,
                    isBackground: true,
                    pdfPage: true, // used for filtering when clearing whole page using Clear All button,
                    loading: false,
                    loaded: true
                }
            };

            return saveElements(page, [element], false);
        },
        [saveElements, uploadImage]
    );

    /**
     * Uploads a PDF page as an image to S3, then creates an
     * image element with that key for the corresponding PDF
     * whiteboard page.
     */
    const putOnPage = useCallback(
        async (page: Page, image, textContent: TextContent, offsetX: number, offsetY: number) => {
            const fileId = nanoid(40);

            const pageText = textContent.items.map((item) => (item as TextItem).str);

            const file: BinaryFileData = {
                mimeType: 'image/png',
                created: Date.now(),
                id: fileId as FileId,
                dataURL: image?.blobUrl as DataURL
            };

            await uploadImage(page, file);

            const previewFile: BinaryFileData = {
                mimeType: 'image/png',
                created: Date.now(),
                id: `${fileId}-preview` as FileId,
                dataURL: image?.previewBlobUrl as DataURL
            };

            await uploadImage(page, previewFile);

            const element: ImageElementWithKey = {
                type: 'image',
                x: offsetX,
                y: offsetY,
                angle: 0,
                backgroundColor: 'transparent',
                boundElements: null,
                fillStyle: 'hachure',
                groupIds: [],
                isDeleted: false,
                link: null,
                locked: true,
                opacity: 100,
                roughness: 1,
                roundness: { type: 1 },
                seed: 0,
                status: 'saved',
                strokeColor: 'transparent',
                strokeStyle: 'solid',
                strokeWidth: 1,
                version: 1,
                versionNonce: 1,
                scale: [1, 1],
                width: image!.width,
                height: image!.height,
                id: fileId,
                fileId: fileId as FileId,
                key: `${fileId}.png`,
                updated: file.created,
                customData: {
                    pageText,
                    pageTextContent: textContent,
                    isBackground: true,
                    pdfPage: true, // used for filtering when clearing whole page using Clear All button
                    loading: false,
                    loaded: true
                }
            };

            await saveElements(page, [element], false);
        },
        [saveElements, uploadImage]
    );

    const uploadPdfPageImages = useCallback(
        async (newPages: Page[], pdfPageImages, pdfPagesTextContents: TextContent[]) => {
            const totalBatches = Math.ceil(newPages.length / IMAGE_UPLOAD_BATCH_SIZE);

            let completedBatches = 0;

            const updateProgress = (batchIndex) => {
                const progressPercentage = (batchIndex / totalBatches) * 100;
                setProgress(progressPercentage);
            };

            for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                const startIndex = batchIndex * IMAGE_UPLOAD_BATCH_SIZE;
                const endIndex = Math.min(startIndex + IMAGE_UPLOAD_BATCH_SIZE, newPages.length);

                const batchPages = newPages.slice(startIndex, endIndex);
                const batchImages = pdfPageImages.slice(startIndex, endIndex);
                const batchTextContents = pdfPagesTextContents.slice(startIndex, endIndex);

                const uploadPromises = batchPages.map(async (page, index) => {
                    await upload(page, batchImages[index], batchTextContents[index]);
                });

                // eslint-disable-next-line no-await-in-loop
                await Promise.all(uploadPromises);

                completedBatches += 1;
                updateProgress(completedBatches);
            }
        },
        [upload]
    );

    const uploadPdfPageImagesToPage = useCallback(
        async (page: Page, pdfPageImages, pdfPagesTextContents: TextContent[]) => {
            const totalBatches = Math.ceil(pdfPageImages.length);

            const allElements = await getElements(page);
            const existingBackgroundElements = allElements.filter((element) => element.body?.customData?.pdfPage);

            let offsetY = 0;
            existingBackgroundElements.forEach((element) => {
                offsetY = Math.max(offsetY, element.body.y + element.body.height);
            });

            const updateProgress = (batchIndex) => {
                const progressPercentage = (batchIndex / totalBatches) * 100;
                setProgress(progressPercentage);
            };

            for (let i = 0; i < pdfPageImages.length; i++) {
                const pageImage = pdfPageImages[i];
                const pageText = pdfPagesTextContents[i];

                // eslint-disable-next-line no-await-in-loop
                await putOnPage(page, pageImage, pageText, 0, offsetY);

                offsetY += pageImage.height + 20;
                updateProgress(i);
            }
        },
        [putOnPage, getElements]
    );

    /**
     * Updates the PDF whiteboard pages to have a positive
     * order, so they may be displayed in the pages list.
     */
    const updatePostProcessedPdfPages = useCallback(
        async (totalPages: number, newPages: Page[]) => {
            const newPageUpdates: Partial<Page>[] = [];

            for (let i = 0; i < totalPages; i += 1) {
                newPageUpdates.push({
                    ...newPages[i],
                    order: -newPages[i].order
                });
            }

            await updatePages(newPageUpdates);
        },
        [updatePages]
    );

    const importPdf = useCallback(
        async (session: Session) => {
            setProgress(0);
            setFileStatus(Status.InProgress);

            const pdfFile = await getPdfFile();
            const { fileName, totalPages, pages } = pdfFile;

            setTotal(totalPages);

            const allPages = await getPages(session.session_id);
            const templatePage = allPages.filter((page) => page.type === PageTypes.Template)[0];

            if (templatePage) {
                const pdfPageImages = await getPdfPageImages(pages);
                const pdfPagesTextContents = await getPdfPagesTextContents(pages);

                await uploadPdfPageImagesToPage(templatePage, pdfPageImages, pdfPagesTextContents);

                const elements = await getElements(whiteboardSessionState.currentPage!);
                excalidrawAPIRef.current!.updateScene({
                    elements: elements.map((e) => e.body),
                    commitToHistory: true
                });
            } else {
                await deletePreviousPdf(session, fileName);
                const newPages = await createPdfPages(session, fileName, totalPages);

                const pdfPageImages = await getPdfPageImages(pages);
                const pdfPagesTextContents = await getPdfPagesTextContents(pages);

                if (newPages) {
                    await uploadPdfPageImages(newPages, pdfPageImages, pdfPagesTextContents);
                    await updatePostProcessedPdfPages(totalPages, newPages);
                }
            }

            setFileStatus(Status.Complete);
        },
        [
            getPages,
            getElements,
            createPdfPages,
            deletePreviousPdf,
            uploadPdfPageImages,
            uploadPdfPageImagesToPage,
            updatePostProcessedPdfPages,
            excalidrawAPIRef,
            whiteboardSessionState.currentPage
        ]
    );

    return {
        deletePdf,
        importPdf,
        fileStatus,
        progress,
        total
    };
};

const WhiteboardPdfContainer = createContainer(useWhiteboardPdf);

export default WhiteboardPdfContainer;
